import { supabase } from '@/integrations/supabase/client';
import { SOURCE_LANGUAGE } from '@/config/languages';

interface TranslationOptions {
  skipCache?: boolean;
  fallback?: string;
  context?: string;
}

const STORAGE_KEY = 'sokoby_translation_cache_v1';
const MAX_BATCH = 25;
const BATCH_DELAY_MS = 40;

type Pending = { text: string; resolve: (v: string) => void };

/**
 * Translation service. Name kept for compatibility with existing imports.
 * Requests are batched per language, cached in memory + localStorage,
 * and always fall back to the original text on failure.
 */
class TranslationService {
  private cache: Record<string, string> = {};
  private queues: Record<string, Pending[]> = {};
  private timers: Record<string, ReturnType<typeof setTimeout>> = {};
  private persistTimer: ReturnType<typeof setTimeout> | null = null;
  private failedUntil = 0;

  constructor() {
    try {
      const raw = typeof localStorage !== 'undefined' ? localStorage.getItem(STORAGE_KEY) : null;
      if (raw) this.cache = JSON.parse(raw);
    } catch {
      this.cache = {};
    }
  }

  public isReady(): boolean {
    return Date.now() >= this.failedUntil;
  }

  private key(text: string, lang: string) {
    return `${lang}::${text}`;
  }

  public getCached(text: string, lang: string): string | undefined {
    if (lang === SOURCE_LANGUAGE) return text;
    return this.cache[this.key(text, lang)];
  }

  private persist() {
    if (this.persistTimer) return;
    this.persistTimer = setTimeout(() => {
      this.persistTimer = null;
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(this.cache));
      } catch {
        // storage full: keep memory cache only
      }
    }, 500);
  }

  async translate(text: string, targetLang: string, options: TranslationOptions = {}): Promise<string> {
    if (targetLang === SOURCE_LANGUAGE || !text?.trim()) return text;
    const cached = !options.skipCache ? this.cache[this.key(text, targetLang)] : undefined;
    if (cached) return cached;
    if (!this.isReady()) return options.fallback || text;

    return new Promise<string>((resolve) => {
      (this.queues[targetLang] ||= []).push({ text, resolve });
      if (this.queues[targetLang].length >= MAX_BATCH) {
        this.flush(targetLang);
      } else if (!this.timers[targetLang]) {
        this.timers[targetLang] = setTimeout(() => this.flush(targetLang), BATCH_DELAY_MS);
      }
    }).then((v) => v || options.fallback || text);
  }

  private async flush(lang: string) {
    clearTimeout(this.timers[lang]);
    delete this.timers[lang];
    const items = (this.queues[lang] || []).splice(0, MAX_BATCH);
    if (this.queues[lang]?.length) this.timers[lang] = setTimeout(() => this.flush(lang), 0);
    if (!items.length) return;

    const unique = Array.from(new Set(items.map((i) => i.text)));
    const call = async (texts: string[]) => {
      const { data, error } = await supabase.functions.invoke('translate', {
        body: { texts, targetLang: lang, sourceLang: SOURCE_LANGUAGE },
      });
      if (error || !Array.isArray(data?.translations) || data.translations.length !== texts.length) {
        throw error || new Error('Bad response');
      }
      return data.translations as string[];
    };
    // A failed batch is split and retried so one bad response never leaves a whole page in French.
    const run = async (texts: string[], depth = 0): Promise<number> => {
      try {
        const out = await call(texts).catch(() => new Promise((r) => setTimeout(r, 500)).then(() => call(texts)));
        texts.forEach((t, i) => { if (out[i] && out[i].trim()) this.cache[this.key(t, lang)] = out[i]; });
        return 0;
      } catch (err) {
        if (texts.length > 1 && depth < 4) {
          const mid = Math.ceil(texts.length / 2);
          const r = await Promise.all([run(texts.slice(0, mid), depth + 1), run(texts.slice(mid), depth + 1)]);
          return r[0] + r[1];
        }
        console.error('Translation error, using original text:', err);
        return texts.length;
      }
    };
    const failed = await run(unique);
    if (failed === unique.length) this.failedUntil = Date.now() + 3_000; // full outage: back off briefly
    this.persist();
    items.forEach((i) => i.resolve(this.cache[this.key(i.text, lang)] || i.text));
  }

  async translateBatch(texts: string[], targetLang: string, options: TranslationOptions = {}) {
    return Promise.all(texts.map((t) => this.translate(t, targetLang, options)));
  }

  async preloadCommonTranslations(texts: string[], targetLang: string) {
    await this.translateBatch(texts, targetLang);
  }

  clearCache() {
    this.cache = {};
    try { localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
  }

  getCacheStats() {
    return { totalCached: Object.keys(this.cache).length, isConfigured: this.isReady() };
  }

  public setApiKey(_key: string) {
    console.warn('setApiKey is deprecated - translation is managed server-side');
  }
}

export const deepLService = new TranslationService();
